import React, { useState } from 'react';
import SEO from '../../components/SEO/SEO';
import './Blog.css';

// Clean SVG Icons (No Emojis)
const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const UserIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const blogPosts = [
  {
    id: 1,
    title: 'The Comprehensive Guide to Commercial CCTV & Security Camera Installation in New York',
    slug: 'commercial-cctv-security-camera-installation-ny-guide',
    category: 'Camera Installation',
    date: 'August 28, 2026',
    author: 'Transcontinental Network Technical Team',
    readTime: '7 min read',
    image: '/images/blog_cctv_installation_ny.jpg',
    excerpt: 'Commercial properties in New York City, Queens, Brooklyn, and Howard Beach require robust, weather-resistant 4K surveillance networks engineered for continuous threat deterrence and legal compliance.',
    content: (
      <>
        <p>
          Managing commercial real estate, retail storefronts, or multi-family residential complexes in <strong>New York City, Queens, Brooklyn, and Howard Beach</strong> requires proactive, high-definition surveillance infrastructure. 
          Installing commercial-grade security cameras is one of the single most cost-effective investments a business owner can make to protect physical assets and limit liability.
        </p>

        <h2>1. Strategic Camera Placement & Threat Deterrence</h2>
        <p>
          Effective security camera layout starts with a comprehensive site assessment. Key coverage zones must include primary entryways, secondary access points, loading docks, cash registers, and perimeter parking lots. 
          Positioning high-definition dome cameras with IK10 vandal-proof ratings at vulnerable points deters unauthorized access before incidents occur.
        </p>

        <h2>2. Weather-Proofing & Coastal Climate Resilience in NY</h2>
        <p>
          New York weather exposes outdoor electronics to severe freeze-thaw cycles, high humidity, and salt air in coastal areas like Howard Beach. 
          Professional installations mandate IP67 weather-rated camera housings, sealed metallic conduit junction boxes, and surge protection devices on all exterior ethernet runs.
        </p>

        <h2>3. 4K Ultra-HD Resolution & Smart Infrared Night Vision</h2>
        <p>
          Unlike consumer DIY wireless cameras, commercial-grade 4K IP cameras feature large optical sensors capable of recording crisp 3840 x 2160 video at 30 frames per second. 
          Equipped with True WDR (Wide Dynamic Range) and Smart IR LEDs, these cameras capture clear facial features and vehicle license plates without overexposure from headlights or glare.
        </p>

        <h2>4. NY Electrical Code & Conduit Requirements</h2>
        <p>
          Commercial installations in New York must adhere strictly to the National Electrical Code (NEC) and local building regulations. 
          Low-voltage cabling routed through plenum spaces must use CMP-rated plenum fire-retardant cabling, and outdoor runs require rigid EMT or PVC conduit to prevent wire damage.
        </p>

        <div className="blog-cta-box">
          <h3>Schedule a Free Commercial Camera Assessment in NY</h3>
          <p>Speak directly with our certified Howard Beach, NY security installation specialists for a detailed on-site assessment and quote.</p>
          <button className="blog-cta-btn" onClick={() => window.location.href = 'tel:+13476682160'}>Call 347.668.2160</button>
        </div>
      </>
    )
  },
  {
    id: 2,
    title: '4K IP Cameras vs. Analog CCTV Systems: Which Surveillance Infrastructure Fits Your Business?',
    slug: '4k-ip-cameras-vs-analog-cctv-systems-comparison',
    category: 'Surveillance Tech',
    date: 'August 24, 2026',
    author: 'Systems Engineering Group',
    readTime: '6 min read',
    image: '/images/blog_ip_camera_vs_analog.jpg',
    excerpt: 'Comparing resolution, Power-over-Ethernet (PoE) cabling, edge AI motion detection, and remote storage to select the ideal security camera platform for your commercial facility.',
    content: (
      <>
        <p>
          Upgrading a commercial facility’s security system requires evaluating two distinct technologies: legacy <strong>Analog CCTV (HD-TVI/CVI)</strong> and modern <strong>Internet Protocol (IP) Camera Systems</strong>. 
          Understanding the architectural differences helps property managers invest wisely.
        </p>

        <h2>Resolution & Detail Retainability</h2>
        <p>
          Analog systems top out around 2MP to 5MP resolution and suffer from signal degradation over long coaxial runs. 
          IP cameras capture pristine digital streams from 4MP up to 12MP (4K UHD), allowing security personnel to digitally zoom into recorded video to examine credit cards, face details, or inventory tags without pixelation.
        </p>

        <h2>Power-over-Ethernet (PoE) Cable Efficiency</h2>
        <p>
          Analog cameras require separate coaxial cables for video transmission and additional 18/2 copper wire pairs for 12V DC power. 
          IP camera infrastructure utilizes a single Cat6 Ethernet cable via Power-over-Ethernet (PoE/PoE+), supplying up to 30W of power alongside gigabit data transmission over distances up to 328 feet (100 meters).
        </p>

        <h2>Edge AI Analytics & Object Classification</h2>
        <p>
          Modern IP cameras incorporate onboard neural processing units (NPU) capable of real-time perimeter protection. 
          Rather than triggering false alerts from wind or shadows, IP camera analytics classify objects into human or vehicle targets, dramatically reducing false alarm notifications.
        </p>

        <div className="blog-cta-box">
          <h3>Upgrade Your Analog Cameras to 4K IP Technology</h3>
          <p>Our team offers hybrid encoders and full IP camera migration packages designed to fit your existing infrastructure budget.</p>
          <button className="blog-cta-btn" onClick={() => window.location.href = 'mailto:Info@transcontinentalnetwork.com'}>Request Migration Quote</button>
        </div>
      </>
    )
  },
  {
    id: 3,
    title: 'Sizing NVR Hard Drive Storage & Channel Capacity for NYC Commercial Properties',
    slug: 'sizing-nvr-hard-drive-storage-nyc-guide',
    category: 'NVR & Recording',
    date: 'August 20, 2026',
    author: 'Storage Architecture Specialists',
    readTime: '8 min read',
    image: '/images/blog_nvr_storage_rack.jpg',
    excerpt: 'Calculate required hard drive terabytes, channel capacity (8, 16, 32, 64-channel NVRs), RAID 5 redundancy, and continuous retention policies for NYC surveillance systems.',
    content: (
      <>
        <p>
          The Network Video Recorder (NVR) is the central storage hub of any IP camera setup. Proper sizing ensures video evidence remains intact and accessible for compliance periods without data loss.
        </p>

        <h2>Calculating Bandwidth & Terabyte Requirements</h2>
        <p>
          A single 4K IP camera running H.265 compression at 20fps requires approximately 6 Mbps bitrate. 
          For a 16-camera installation recording continuously 24/7 for 30 days, the total required storage space is approximately 14 to 16 Terabytes.
        </p>

        <h2>Surveillance-Grade Hard Drives vs. Standard Desktop Drives</h2>
        <p>
          Desktop hard drives are designed for light 8x5 operational cycles and will fail prematurely in surveillance environments. 
          NVR systems require specialized surveillance drives (e.g., Western Digital Purple or Seagate SkyHawk) built for continuous 24/7 writing, multi-drive vibration mitigation, and high heat tolerance.
        </p>

        <h2>RAID Configuration & Hardware Redundancy</h2>
        <p>
          Enterprise NVRs supporting 32 or 64 channels utilize hardware RAID 5 or RAID 6 arrays. 
          If a single hard drive experiences mechanical failure, the RAID array maintains full video recording and allows hot-swapping the failed drive with zero data downtime.
        </p>

        <div className="blog-cta-box">
          <h3>Need Professional NVR Storage Calculations?</h3>
          <p>Our engineers calculate exact storage, bandwidth, and RAID configurations for your specific security scope.</p>
          <button className="blog-cta-btn" onClick={() => window.location.href = 'tel:+13476682160'}>Call 347.668.2160</button>
        </div>
      </>
    )
  },
  {
    id: 4,
    title: 'Cat6 Cabling & Power-over-Ethernet (PoE) Best Practices for Security Camera Wiring',
    slug: 'cat6-cabling-poe-best-practices-security-camera-wiring',
    category: 'Network Infrastructure',
    date: 'August 16, 2026',
    author: 'Network Infrastructure Engineering',
    readTime: '6 min read',
    image: '/images/blog_network_cabling_poe.jpg',
    excerpt: 'Key guidelines for low-voltage wiring, 100m distance limits, plenum fire-safety ratings, patch panel organization, and surge protection for camera networks.',
    content: (
      <>
        <p>
          High-definition IP security cameras rely heavily on structured copper cabling. Poor wiring, improper termination, or substandard cables lead to packet loss, video flickering, and camera power drops.
        </p>

        <h2>Solid Copper vs. Copper Clad Aluminum (CCA) Cables</h2>
        <p>
          Cheap Copper Clad Aluminum (CCA) cables pose serious fire hazards and suffer high DC resistance over Power-over-Ethernet. 
          Professional camera installations strictly require 100% Solid Bare Copper Cat6 cables certified for IEEE 802.3at/bt high-power PoE devices.
        </p>

        <h2>Understanding Distance Limits & PoE Extenders</h2>
        <p>
          Standard ethernet cabling has a maximum certified distance limit of 328 feet (100 meters). 
          For runs exceeding 328 feet across long parking lots or warehouse facilities, specialized PoE extenders or fiber optic backbones with remote PoE switches must be deployed.
        </p>

        <h2>Patch Panel Management & Cable Labeling</h2>
        <p>
          All camera homeruns should terminate cleanly into a rack-mounted RJ45 patch panel inside a locked IT cabinet. 
          Each cable end must be labeled matching camera IDs to simplify troubleshooting and long-term network maintenance.
        </p>

        <div className="blog-cta-box">
          <h3>Professional Structured Cabling Services in NY</h3>
          <p>We install Cat6 cabling, patch panels, and server racks engineered to industry standards.</p>
          <button className="blog-cta-btn" onClick={() => window.location.href = 'tel:+13476682160'}>Get Cabling Quote</button>
        </div>
      </>
    )
  },
  {
    id: 5,
    title: 'Configuring Secure Remote Viewing & Real-Time Mobile Alerts for CCTV Systems',
    slug: 'configuring-secure-remote-viewing-real-time-mobile-alerts',
    category: 'Remote Monitoring',
    date: 'August 11, 2026',
    author: 'Cybersecurity & Application Development',
    readTime: '5 min read',
    image: '/images/blog_remote_monitoring_app.jpg',
    excerpt: 'How to safely configure mobile monitoring apps, encrypted P2P cloud relay, push notification alerts, and cybersecurity protocols without opening dangerous firewall ports.',
    content: (
      <>
        <p>
          Remote accessibility is a vital feature of modern commercial surveillance. Property owners can monitor live camera feeds and review recorded events from any location using smartphone and tablet applications.
        </p>

        <h2>Avoiding Risky Port Forwarding</h2>
        <p>
          Legacy camera setups relied on open router ports (port forwarding), exposing recorders to automated brute-force botnets. 
          Modern installations utilize secure P2P cloud relay services or encrypted SSL/TLS VPN tunnels to establish authenticated sessions without exposing local networks.
        </p>

        <h2>Push Notifications & Event Search Filters</h2>
        <p>
          Configuring smart motion zones allows users to receive instant push alerts on mobile devices when line-crossing or intrusion boundaries are triggered after business hours. 
          Users can filter recorded footage instantly by event type, date, or specific camera channel.
        </p>

        <h2>Multi-User Role Access Management</h2>
        <p>
          System administrators can assign specific permission levels for different staff members. 
          For instance, security guards can be granted live view access only, while store managers retain permission to export recorded video clips.
        </p>

        <div className="blog-cta-box">
          <h3>Need Mobile Monitoring Configured?</h3>
          <p>Our tech team configures secure mobile applications, remote alerts, and encrypted web connections for your system.</p>
          <button className="blog-cta-btn" onClick={() => window.location.href = 'tel:+13476682160'}>Call 347.668.2160</button>
        </div>
      </>
    )
  },
  {
    id: 6,
    title: 'Why Licensed & Accredited Technicians Are Essential for NY Security & Alarm Installation',
    slug: 'why-licensed-accredited-technicians-essential-ny-security',
    category: 'Licensing & Compliance',
    date: 'August 06, 2026',
    author: 'Compliance & Quality Control',
    readTime: '7 min read',
    image: '/certificates/certificate_1.jpeg',
    excerpt: 'Protecting your commercial property investments by hiring state-licensed security technicians with accredited degrees in Information Technology and electrical code compliance.',
    content: (
      <>
        <p>
          In New York State, commercial low-voltage wiring, video surveillance installation, and alarm integration require strict adherence to state licensing laws and municipal building codes.
        </p>

        <h2>Degree-Accredited IT Technicians</h2>
        <p>
          At Transcontinental Network, our technical team combines rigorous academic training—including accredited degrees in Information Technology—with official state licenses in Security & Fire Alarm Systems.
        </p>

        <h2>Legal Liability & Insurance Protection</h2>
        <p>
          Hiring uncertified contractors risks voided equipment warranties, electrical code violations, and compromised network security. 
          Our fully licensed and insured technicians guarantee code compliance, professional installation standards, and ongoing warranty protection.
        </p>

        <div className="blog-cta-box">
          <h3>Partner with Certified NY Security Specialists</h3>
          <p>Review our credentials on our Certificates page or contact us to discuss your upcoming installation project.</p>
          <button className="blog-cta-btn" onClick={() => window.location.href = 'tel:+13476682160'}>Contact Our Team</button>
        </div>
      </>
    )
  },
  {
    id: 7,
    title: 'Integrating Access Control & Video Surveillance for Commercial Office Security in Queens, NY',
    slug: 'integrating-access-control-video-surveillance-queens-ny',
    category: 'Access Control',
    date: 'August 02, 2026',
    author: 'Integrated Security Solutions Team',
    readTime: '6 min read',
    image: '/images/hero_installation.png',
    excerpt: 'Synchronize keycard readers, smart keypads, magnetic door locks, and CCTV video verification to audit employee access and secure commercial office space.',
    content: (
      <>
        <p>
          Combining electronic access control with IP video surveillance creates a unified defense layer for modern commercial facilities in Queens and Brooklyn.
        </p>

        <h2>Video Verification of Door Access Events</h2>
        <p>
          When an employee swipes an access card or enters a PIN at a secure door, the access controller communicates with the NVR. 
          The camera pointed at the doorway automatically logs a timestamped video bookmark, allowing management to visually verify door activity and prevent card sharing.
        </p>

        <h2>Emergency Lockdown & Fire Alarm Interlock</h2>
        <p>
          Integrated access control panels automatically release magnetic locks upon fire alarm activation to comply with life safety codes while sending instant alert notifications to designated security personnel.
        </p>

        <div className="blog-cta-box">
          <h3>Design an Integrated Access & Camera System</h3>
          <p>Contact our experts to design a unified access control and video surveillance solution for your building.</p>
          <button className="blog-cta-btn" onClick={() => window.location.href = 'tel:+13476682160'}>Call 347.668.2160</button>
        </div>
      </>
    )
  },
  {
    id: 8,
    title: 'Preventative Maintenance Checklist for Commercial CCTV Systems in New York',
    slug: 'preventative-maintenance-checklist-commercial-cctv-systems-ny',
    category: 'System Maintenance',
    date: 'July 27, 2026',
    author: 'Technical Support Services',
    readTime: '5 min read',
    image: '/images/hero_cctv_product.png',
    excerpt: 'Routine maintenance steps including camera glass cleaning, junction box seal inspection, hard drive health diagnostics, and NVR firmware security patches.',
    content: (
      <>
        <p>
          A security camera system is only effective if it functions flawlessly during a critical event. Regular preventative maintenance prevents premature equipment failure and ensures continuous recording.
        </p>

        <h2>1. Semi-Annual Optical Lens Cleaning</h2>
        <p>
          Outdoor camera domes collect road grime, dust, and spider webs over time. Cleaning optical glass covers with microfiber cloths and specialized anti-static solutions restores crystal-clear night vision illumination.
        </p>

        <h2>2. NVR Hard Drive S.M.A.R.T. Health Audits</h2>
        <p>
          Checking S.M.A.R.T. disk diagnostics inside the NVR monthly detects bad sectors or impending drive failure before video data is permanently lost.
        </p>

        <h2>3. Firmware & Cybersecurity Updates</h2>
        <p>
          Applying official manufacturer firmware updates closes known software vulnerabilities and maintains system stability across network routers and recorders.
        </p>

        <div className="blog-cta-box">
          <h3>Request a Maintenance Service Visit</h3>
          <p>Keep your surveillance system operating at peak reliability with our scheduled maintenance plans.</p>
          <button className="blog-cta-btn" onClick={() => window.location.href = 'tel:+13476682160'}>Schedule Maintenance</button>
        </div>
      </>
    )
  }
];

const Blog = ({ setCurrentPage }) => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [activePost, setActivePost] = useState(null);

  const categories = [
    'All', 
    'Camera Installation', 
    'Surveillance Tech', 
    'NVR & Recording', 
    'Network Infrastructure',
    'Remote Monitoring',
    'Licensing & Compliance',
    'Access Control',
    'System Maintenance'
  ];

  const filteredPosts = blogPosts.filter((post) => {
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="blog-page">
      <SEO 
        title="Security Camera Installation Blog & Guides NYC | Transcontinental Network"
        description="Expert technical articles on CCTV camera installation in New York (NYC, Queens, Howard Beach, Brooklyn), 4K IP cameras, NVR storage calculations, Cat6 PoE wiring, and state security compliance."
        keywords="security camera installation blog NY, CCTV installation NYC guide, camera installer Howard Beach, NVR storage calculator, Cat6 PoE wiring NY, commercial surveillance blog"
        canonical="https://transcontinentalnetwork.com/blog"
      />

      {/* Hero Banner */}
      <section className="blog-hero">
        <div className="container">
          <span className="blog-hero-badge">Surveillance & IT Technical Articles</span>
          <h1 className="page-title">Security Camera & IT Knowledge Base</h1>
          <p className="page-subtitle">
            In-depth technical guides, local New York commercial security standards, and expert surveillance setup advice.
          </p>
        </div>
      </section>

      <div className="container">
        {/* Search & Filter Bar */}
        <div className="blog-search-wrapper">
          <div className="blog-search-input-container">
            <span className="blog-search-icon"><SearchIcon /></span>
            <input 
              type="text" 
              placeholder="Search camera guides, NVR setup, Cat6 wiring, NY security..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="blog-search-input"
            />
            {searchQuery && (
              <button className="blog-search-clear" onClick={() => setSearchQuery('')}>Clear</button>
            )}
          </div>
        </div>

        {/* Category Filter Tabs */}
        <div className="blog-categories">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`blog-category-btn ${selectedCategory === cat ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Blog Post Grid */}
        {filteredPosts.length === 0 ? (
          <div className="blog-no-results">
            <h3>No Articles Found</h3>
            <p>No guides matched your search term "{searchQuery}". Try clearing your search or picking another category.</p>
            <button className="blog-reset-btn" onClick={() => { setSelectedCategory('All'); setSearchQuery(''); }}>Reset Filters</button>
          </div>
        ) : (
          <div className="blog-grid">
            {filteredPosts.map((post) => (
              <article key={post.id} className="blog-card">
                <div className="blog-card-image-wrapper">
                  <img 
                    src={post.image} 
                    alt={`${post.title} - Transcontinental Network`} 
                    className="blog-card-image"
                    loading="lazy"
                  />
                  <span className="blog-card-tag">{post.category}</span>
                </div>

                <div className="blog-card-content">
                  <div className="blog-card-meta">
                    <span className="blog-card-meta-item"><CalendarIcon /> {post.date}</span>
                    <span className="blog-card-meta-item"><ClockIcon /> {post.readTime}</span>
                  </div>

                  <h2 className="blog-card-title">{post.title}</h2>
                  <p className="blog-card-excerpt">{post.excerpt}</p>

                  <div className="blog-card-footer">
                    <button className="blog-read-btn" onClick={() => setActivePost(post)}>
                      Read Full Article <ArrowRightIcon />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {/* Modal Article Reader */}
      {activePost && (
        <div className="blog-modal-overlay" onClick={() => setActivePost(null)}>
          <div className="blog-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="blog-modal-close" 
              onClick={() => setActivePost(null)}
              aria-label="Close Article"
            >
              ✕
            </button>

            <div className="blog-article-header">
              <span className="blog-article-tag">{activePost.category}</span>
              <h1 className="blog-article-title">{activePost.title}</h1>
              <div className="blog-article-meta">
                <span className="blog-article-meta-item"><UserIcon /> {activePost.author}</span>
                <span className="blog-article-meta-item"><CalendarIcon /> {activePost.date}</span>
                <span className="blog-article-meta-item"><ClockIcon /> {activePost.readTime}</span>
              </div>
            </div>

            <div className="blog-article-body">
              {activePost.content}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Blog;
